import { ReactNode, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import { logoutRequest } from '@paradigm/store/auth';

import { AuthUser } from '@paradigm/store/src/auth/types';
import { useTrackedCallback } from '@paradigm/features/src/analytics';
import SettingsModal, { SettingsView } from './SettingsModal';

interface Props {
  user: AuthUser;
  handleLogout: () => void;
  children: ReactNode;
}

// `?settings=<key>` deep-link → open the settings modal at a sub-view.
// External callers (Stripe success/cancel/portal-return URLs) drop the
// user back into the right tab without us having to invent a routed
// page. Keep the keys stable + minimal — they're part of a URL.
const SETTINGS_VIEW_FROM_QUERY: Record<string, SettingsView> = {
  billing: 'Terminal Subscription',
};

const SettingsContainerPure = ({ user, handleLogout, children }: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [initialView, setInitialView] = useState<SettingsView | undefined>();
  const onOpen = useTrackedCallback('view settings', () => {
    // Manual open shouldn't carry the auto-open initialView from a
    // prior Stripe return — start at the main settings list.
    setInitialView(undefined);
    setIsOpen(true);
  });
  const onClose = useTrackedCallback('close modal', () => setIsOpen(false));

  const location = useLocation();
  const history = useHistory();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const raw = params.get('settings');
    if (raw == null) return;
    const view = SETTINGS_VIEW_FROM_QUERY[raw];
    if (view == null) return;
    setInitialView(view);
    setIsOpen(true);
    // Strip the query param so a refresh doesn't re-open the modal,
    // and so a manual close + revisit of the same link reopens
    // correctly (the next visit re-adds the param, which then
    // re-fires this effect).
    params.delete('settings');
    const search = params.toString();
    history.replace({
      pathname: location.pathname,
      search: search.length === 0 ? '' : `?${search}`,
    });
  }, [location.search, location.pathname, history]);

  return (
    <>
      <button
        className="settings-trigger"
        onClick={onOpen}
        onMouseDown={(e) => {
          e.preventDefault(); // prevent chat input from losing focus
        }}
      >
        {children}
      </button>
      {isOpen && (
        <SettingsModal
          onClose={onClose}
          onLogout={handleLogout}
          user={user}
          initialView={initialView}
        />
      )}
    </>
  );
};

const mapStateToProps = (state: { auth: { user: AuthUser } }) => ({
  user: state.auth.user,
});

const SettingsContainer = connect(mapStateToProps, {
  handleLogout: () => logoutRequest({ reason: 'user-triggered' }),
})(SettingsContainerPure);
export default SettingsContainer;
